import { useState } from "react";

import { ApiError, api } from "../api/client";
import { BrandMark } from "../components/icons";
import { useI18n, type Translate } from "../i18n";

/**
 * Why a sign-in was refused, in words the customer can act on.
 *
 * The password *was* right for `tenant_expired`, `tenant_suspended` and
 * `console_blocked` (that is the whole point of the server answering 403 rather
 * than 401), so telling someone to check their password would send them the
 * wrong way entirely. The same wording is reused when one of those codes ends
 * a session that was already open.
 */
export function signInMessage(error: unknown, t: Translate): string {
  if (!(error instanceof ApiError)) {
    return error instanceof Error ? error.message : t("login.failed");
  }
  switch (error.code) {
    case "tenant_expired":
      return error.expiresAt
        ? t("login.expired", { date: new Date(error.expiresAt).toLocaleDateString() })
        : t("login.expiredNoDate");
    case "tenant_suspended":
      return t("login.suspended");
    case "console_blocked": {
      // A second sentence, not a parenthesis: the reason is written by whoever
      // closed the console and is the only part the customer can act on.
      const reason = error.detail?.reason;
      const closed = t("login.consoleBlocked");
      return typeof reason === "string" && reason ? `${closed} ${reason}` : closed;
    }
    case "too_many_attempts":
      return t("login.tooManyAttempts");
    // A tab our own console opened, whose ticket was spent or stale by the
    // time it arrived (B2B §7.1). A customer never meets this one.
    case "handoff_expired":
      return t("login.handoffExpired");
    default:
      return error.message || t("login.failed");
  }
}

/**
 * The door: a wordmark, two fields, and one line saying where the password
 * came from — accounts here are issued by us, so "I never signed up" is the
 * question this screen actually has to answer.
 *
 * `notice` is why the customer was sent back here mid-session, if they were.
 */
export default function Login({
  onSignedIn,
  notice,
}: {
  onSignedIn: () => void;
  notice?: unknown;
}) {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // `undefined` means nothing has been tried yet, so the notice still stands;
  // a submit replaces it either way. Kept as the error rather than its wording
  // so switching language re-words the line that is already on screen.
  const [attempt, setAttempt] = useState<unknown>();
  const [busy, setBusy] = useState(false);
  const refusal = attempt === undefined ? notice : attempt;
  const error = refusal ? signInMessage(refusal, t) : null;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setAttempt(null);
    try {
      await api.login(email, password);
      onSignedIn();
    } catch (exc) {
      setAttempt(exc);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <form onSubmit={submit}>
        <div className="login-card">
          <div className="brand">
            <BrandMark size={34} />
            <span className="brand-text">
              <span className="brand-name">{t("app.name")}</span>
            </span>
          </div>
          <h2>{t("login.title")}</h2>
          <p className="login-lede">{t("login.subtitle")}</p>
          <label>
            {t("login.email")}
            <input
              type="email"
              value={email}
              autoFocus
              autoComplete="email"
              placeholder={t("login.emailPlaceholder")}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <label>
            {t("login.password")}
            <input
              type="password"
              value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
          {error && (
            <p className="login-error" role="alert">
              {error}
            </p>
          )}
          <button className="primary" type="submit" disabled={busy}>
            {busy ? t("login.signingIn") : t("login.signIn")}
          </button>
        </div>
      </form>
    </div>
  );
}
